/**
 * Country / language pop-up (snippets/localization-popup.liquid)
 * - Opens only on click of any [data-localization-open] (header, menu, footer)
 * - Country list = one option per market: single-country markets by country name,
 *   multi-country markets (International) as one option
 * - The language list follows the selected market
 * - Custom dropdowns (button + listbox) instead of the native select pop-up
 * - Apply submits Shopify's localization form, which redirects to the current page
 *   in the chosen market + language
 */
(function () {
  const modal = document.querySelector('[data-localization-modal]');
  if (!modal) return;

  let data;
  try {
    data = JSON.parse(modal.querySelector('[data-localization-data]').textContent);
  } catch (e) {
    return;
  }

  // Group countries by market -----------------------------------------------
  const markets = [];
  const byHandle = new Map();
  data.countries.forEach((country) => {
    let market = byHandle.get(country.market);
    if (!market) {
      market = { handle: country.market, countries: [] };
      byHandle.set(country.market, market);
      markets.push(market);
    }
    market.countries.push(country);
  });
  markets.forEach((market) => {
    market.isGroup = market.countries.length > 1;
    market.label = market.isGroup ? data.internationalLabel : market.countries[0].name;
    market.languages = market.countries[0].languages;
  });
  // Named countries A-Z, International last
  markets.sort((a, b) => (a.isGroup - b.isGroup) || a.label.localeCompare(b.label));

  const marketOfCountry = (code) => markets.find((m) => m.countries.some((c) => c.code === code));

  // Country submitted for a market. For International: the visitor's current
  // country if it's in there, else the country from the browser language
  // (e.g. en-GB -> GB), else the market's first country.
  const countryFor = (market) => {
    if (!market.isGroup) return market.countries[0].code;
    const codes = market.countries.map((c) => c.code);
    if (codes.includes(data.current.country)) return data.current.country;
    const browserRegion = (navigator.languages || [navigator.language])
      .map((l) => (l.split('-')[1] || '').toUpperCase())
      .find((r) => codes.includes(r));
    return browserRegion || codes[0];
  };

  // Custom dropdown -----------------------------------------------------------
  class Dropdown {
    constructor(root, onChange) {
      this.root = root;
      this.button = root.querySelector('.localization-dropdown__button');
      this.list = root.querySelector('.localization-dropdown__list');
      this.valueEl = root.querySelector('[data-localization-value]');
      this.input = root.querySelector('input[type="hidden"]');
      this.onChange = onChange;
      this.options = [];
      this.activeIndex = -1;

      this.button.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()));
      this.button.addEventListener('keydown', (e) => {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
          e.preventDefault();
          this.open();
        }
      });
      this.list.addEventListener('keydown', (e) => this.onListKey(e));
      this.list.addEventListener('click', (e) => {
        const li = e.target.closest('[role="option"]');
        if (li) this.select(Number(li.dataset.index));
      });
    }

    setOptions(options, selectedValue) {
      this.options = options;
      this.list.replaceChildren(...options.map((option, index) => {
        const li = document.createElement('li');
        li.id = `${this.list.getAttribute('aria-labelledby')}-${index}`;
        li.className = 'localization-dropdown__option';
        li.setAttribute('role', 'option');
        li.dataset.index = index;
        li.textContent = option.label;
        return li;
      }));
      const index = Math.max(0, options.findIndex((o) => o.value === selectedValue));
      this.select(index, { silent: true, keepOpen: true });
    }

    select(index, { silent = false, keepOpen = false } = {}) {
      const option = this.options[index];
      if (!option) return;
      this.selectedIndex = index;
      this.valueEl.textContent = option.label;
      this.list.querySelectorAll('[role="option"]').forEach((li, i) => li.setAttribute('aria-selected', String(i === index)));
      if (!silent && this.onChange) this.onChange(option);
      if (!keepOpen) this.close({ focusButton: true });
    }

    isOpen() {
      return !this.list.hidden;
    }

    open() {
      dropdowns.forEach((d) => d !== this && d.close());
      this.list.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      this.setActive(this.selectedIndex);
      this.list.focus({ preventScroll: true });
    }

    close({ focusButton = false } = {}) {
      if (!this.isOpen()) return;
      this.list.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      this.list.removeAttribute('aria-activedescendant');
      if (focusButton) this.button.focus({ preventScroll: true });
    }

    setActive(index) {
      const items = this.list.querySelectorAll('[role="option"]');
      if (!items.length) return;
      this.activeIndex = Math.min(Math.max(index, 0), items.length - 1);
      items.forEach((li, i) => li.classList.toggle('is-active', i === this.activeIndex));
      const active = items[this.activeIndex];
      this.list.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }

    onListKey(e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); this.setActive(this.activeIndex + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.setActive(this.activeIndex - 1); }
      else if (e.key === 'Home') { e.preventDefault(); this.setActive(0); }
      else if (e.key === 'End') { e.preventDefault(); this.setActive(this.options.length - 1); }
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.select(this.activeIndex); }
      else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); this.close({ focusButton: true }); }
      else if (e.key === 'Tab') { this.close(); }
      else if (e.key.length === 1) {
        // Type-ahead: jump to the first option starting with the typed letter
        const i = this.options.findIndex((o) => o.label.toLowerCase().startsWith(e.key.toLowerCase()));
        if (i !== -1) this.setActive(i);
      }
    }
  }

  const languageDropdown = new Dropdown(modal.querySelector('[data-localization-dropdown="language"]'), (option) => {
    languageDropdown.input.value = option.value;
  });

  const setLanguages = (market) => {
    const current = languageDropdown.input.value;
    const options = market.languages.map((l) => ({ value: l.code, label: l.name }));
    // Keep the chosen language if the market has it, else the market's first language
    const selected = options.some((o) => o.value === current) ? current : options[0]?.value;
    languageDropdown.setOptions(options, selected);
    languageDropdown.input.value = selected;
  };

  const countryDropdown = new Dropdown(modal.querySelector('[data-localization-dropdown="country"]'), (option) => {
    const market = markets.find((m) => m.handle === option.value);
    countryDropdown.input.value = countryFor(market);
    setLanguages(market);
  });

  const dropdowns = [countryDropdown, languageDropdown];

  const reset = () => {
    const currentMarket = marketOfCountry(data.current.country) || markets[0];
    countryDropdown.setOptions(markets.map((m) => ({ value: m.handle, label: m.label })), currentMarket.handle);
    countryDropdown.input.value = countryFor(currentMarket);
    languageDropdown.input.value = data.current.language;
    setLanguages(currentMarket);
  };

  // Pop-up ------------------------------------------------------------------
  let lastTrigger = null;

  const open = (trigger) => {
    lastTrigger = trigger;
    reset();
    modal.hidden = false;
    document.body.classList.add('localization-modal-open');
    requestAnimationFrame(() => modal.classList.add('is-open'));
    countryDropdown.button.focus({ preventScroll: true });
  };

  const close = () => {
    dropdowns.forEach((d) => d.close());
    modal.classList.remove('is-open');
    document.body.classList.remove('localization-modal-open');
    modal.hidden = true;
    lastTrigger?.focus({ preventScroll: true });
  };

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-localization-open]');
    if (trigger) {
      e.preventDefault();
      open(trigger);
      return;
    }
    if (e.target.closest('[data-localization-close]')) {
      close();
      return;
    }
    // Click outside an open dropdown closes it
    dropdowns.forEach((d) => { if (!d.root.contains(e.target)) d.close(); });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hidden) close();
  });
})();
